import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyIcon from '@mui/icons-material/Key';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatKstDate, formatRelative, formatNumber, summarizeUserAgent } from '../../utils/formatUtils';
import {
  UserStatusChip,
  SubscriptionChip,
  ClientChip,
  USER_STATUS_OPTIONS,
  auditLabel,
  useConfirm,
  useNotify,
} from '../../components/common';
import { GrantSubscriptionDialog, ExtendSubscriptionDialog, pendingActions } from '../../components/SubscriptionDialogs';

const EmptyRow = ({ cols, text }) => (
  <TableRow>
    <TableCell colSpan={cols} align="center" sx={{ color: 'text.secondary', py: 3 }}>
      {text}
    </TableCell>
  </TableRow>
);

const ProfileTab = ({ detail, reload }) => {
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const u = detail.user;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  useEffect(() => {
    setForm({
      username: u.username || '',
      phoneNumber: u.phoneNumber || '',
      status: u.status || 'active',
      emailVerified: !!u.emailVerifiedAt,
      adminMemo: u.adminMemo || '',
    });
  }, [u]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/admin/users/${encodeURIComponent(u.id)}`, form);
      notify(res.data.sessionsClosed ? `${res.data.message} (세션 ${res.data.sessionsClosed}개 종료)` : res.data.message);
      reload();
    } catch (e) {
      notify(getErrorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (form.status !== u.status && form.status !== 'active') {
      ask({
        title: '회원 상태 변경',
        message:
          `회원을 '${USER_STATUS_OPTIONS.find((o) => o.value === form.status)?.label}' 상태로 바꿉니다.\n` +
          '· 모든 로그인 세션이 즉시 종료되고 로그인이 차단됩니다.\n' +
          '· 에이전트는 다음 동기화(최대 10분) 때 무료 플랜으로 내려가 자동매매를 끕니다.',
        confirmText: '변경',
        danger: true,
        onConfirm: save,
      });
    } else {
      save();
    }
  };

  const resetPassword = () =>
    ask({
      title: '비밀번호 초기화',
      message: '임시 비밀번호를 발급하고 회원의 모든 세션을 종료합니다. 계속할까요?',
      confirmText: '초기화',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/users/${encodeURIComponent(u.id)}/reset-password`, {});
          setTempPassword(res.data.temporaryPassword);
          reload();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  const logoutAll = () =>
    ask({
      title: '전체 로그아웃',
      icon: <LogoutIcon color="error" />,
      message: '이 회원의 모든 기기(웹·에이전트 로그인 세션)를 로그아웃합니다.',
      confirmText: '로그아웃',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/users/${encodeURIComponent(u.id)}/logout-all`);
          notify(res.data.message);
          reload();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            기본 정보
          </Typography>
          <Stack spacing={2}>
            <TextField label="이메일 (로그인 ID)" value={u.email} disabled />
            <TextField label="사용자명" value={form.username || ''} onChange={set('username')} />
            <TextField label="휴대폰" value={form.phoneNumber || ''} onChange={set('phoneNumber')} placeholder="01012345678" />
            <TextField select label="회원 상태" value={form.status || 'active'} onChange={set('status')}>
              {USER_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Switch checked={!!form.emailVerified} onChange={(e) => setForm((f) => ({ ...f, emailVerified: e.target.checked }))} />}
              label={`이메일 인증 ${u.emailVerifiedAt ? `(${formatKstDateTime(u.emailVerifiedAt)})` : '(미인증 — 켜면 수동 인증 처리)'}`}
            />
            <TextField label="관리자 메모 (회원에게 보이지 않음)" value={form.adminMemo || ''} onChange={set('adminMemo')} multiline minRows={3} />
            <Box>
              <Button variant="contained" onClick={onSave} disabled={saving}>
                저장
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            계정 정보
          </Typography>
          {[
            ['회원 ID', u.id],
            ['가입일', formatKstDateTime(u.createdAt)],
            ['최근 로그인', `${formatKstDateTime(u.lastLoginAt)} (${formatRelative(u.lastLoginAt)})`],
            ['정보 수정일', formatKstDateTime(u.updatedAt)],
            ['권한', u.isAdmin ? '관리자' : '일반 회원'],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {k}
              </Typography>
              <Typography variant="body2">{v}</Typography>
            </Box>
          ))}
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            보안 조치
          </Typography>
          <Stack spacing={1}>
            <Button variant="outlined" color="warning" onClick={resetPassword}>
              비밀번호 초기화 (임시 비밀번호 발급)
            </Button>
            <Button variant="outlined" color="error" onClick={logoutAll}>
              모든 기기 로그아웃
            </Button>
          </Stack>
        </Paper>
      </Grid>
      {confirmDialog}
      <Dialog open={!!tempPassword} onClose={() => setTempPassword(null)}>
        <DialogTitle>
          <KeyIcon color="primary" />
          임시 비밀번호
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            이 창을 닫으면 다시 볼 수 없습니다. 회원에게 안전한 경로로 전달하고 로그인 후 변경하도록 안내하세요.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
              {tempPassword}
            </Typography>
            <IconButton onClick={() => navigator.clipboard?.writeText(tempPassword).then(() => notify('복사했습니다.'))}>
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTempPassword(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const SubscriptionTab = ({ detail, reload }) => {
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const s = detail.subscription;
  const pending = pendingActions({ ask, notify, onDone: reload });

  const cancel = (sub) =>
    ask({
      title: '구독 취소',
      message: `${sub.planName} 구독을 즉시 취소합니다.\n회원의 에이전트는 다음 동기화 때 자동매매를 끕니다.`,
      confirmText: '구독 취소',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/subscriptions/${sub.id}/cancel`);
          notify(res.data.message, res.data.ok ? 'success' : 'warning');
          reload();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              현재 플랜: {s.subscriptionPlanName} {s.subscriptionCycle ? `(${s.subscriptionCycle})` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {s.subscription === 'Y'
                ? `${formatKstDate(s.subscriptionStartedAt)} ~ ${formatKstDate(s.subscriptionExpiresAt)}`
                : '무료 회원'}{' '}
              · 관심종목 {s.maxWatchlist ?? '무제한'} · 자동매매 {s.maxAutoTrading ?? '무제한'}
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => setGrantOpen(true)}>
            구독 부여
          </Button>
        </Box>
      </Paper>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>요금제</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>시작</TableCell>
              <TableCell>만료</TableCell>
              <TableCell align="right">금액</TableCell>
              <TableCell>결제번호 / 메모</TableCell>
              <TableCell align="right">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.subscriptions.length === 0 && <EmptyRow cols={8} text="구독 이력이 없습니다." />}
            {detail.subscriptions.map((sub) => (
              <TableRow key={sub.id}>
                <TableCell>{sub.id}</TableCell>
                <TableCell>
                  {sub.planName} ({sub.billingCycleLabel})
                </TableCell>
                <TableCell>
                  <SubscriptionChip status={sub.status} />
                </TableCell>
                <TableCell>{formatKstDate(sub.startedAt)}</TableCell>
                <TableCell>{formatKstDate(sub.expiresAt)}</TableCell>
                <TableCell align="right">{sub.amount != null ? `${formatNumber(sub.amount)}원` : '-'}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {[sub.paymentId, sub.memo].filter(Boolean).join(' / ') || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {sub.status === 'pending' && (
                    <>
                      <Button size="small" variant="contained" onClick={() => pending.approve(sub)}>
                        승인
                      </Button>
                      <Button size="small" color="error" onClick={() => pending.reject(sub)}>
                        거절
                      </Button>
                    </>
                  )}
                  {(sub.status === 'active' || sub.status === 'expired') && (
                    <Button size="small" onClick={() => setExtendTarget(sub)}>
                      기간 변경
                    </Button>
                  )}
                  {sub.status === 'active' && (
                    <Button size="small" color="error" onClick={() => cancel(sub)}>
                      취소
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <GrantSubscriptionDialog open={grantOpen} user={detail.user} onClose={() => setGrantOpen(false)} onDone={reload} />
      <ExtendSubscriptionDialog
        open={!!extendTarget}
        subscription={extendTarget}
        onClose={() => setExtendTarget(null)}
        onDone={reload}
      />
      {confirmDialog}
    </>
  );
};

const AccessTab = ({ detail, reload }) => {
  const notify = useNotify();
  const kill = async (s) => {
    try {
      const res = await apiClient.delete(`/admin/sessions/${s.sessionId}`, { data: { userId: detail.user.id } });
      notify(res.data.message);
      reload();
    } catch (e) {
      notify(getErrorMessage(e), 'error');
    }
  };
  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          최근 세션 (20개)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>구분</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>기기</TableCell>
              <TableCell>로그인</TableCell>
              <TableCell>마지막 활동</TableCell>
              <TableCell>로그아웃</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.sessions.length === 0 && <EmptyRow cols={8} text="세션 기록이 없습니다." />}
            {detail.sessions.map((s) => {
              const live = s.isActive && (!s.expiresAt || new Date(s.expiresAt) > new Date());
              return (
                <TableRow key={s.sessionId}>
                  <TableCell>
                    <ClientChip client={s.client} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={live ? '접속중' : '종료'} color={live ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>{s.ipAddress}</TableCell>
                  <TableCell>
                    <Tooltip title={s.userAgent || ''}>
                      <span>{summarizeUserAgent(s.userAgent)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatKstDateTime(s.loginTime)}</TableCell>
                  <TableCell>{formatRelative(s.lastActivityAt)}</TableCell>
                  <TableCell>{formatKstDateTime(s.logoutTime)}</TableCell>
                  <TableCell align="right">
                    {live && (
                      <Button size="small" color="error" onClick={() => kill(s)}>
                        종료
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          로그인 이력 (최근 50건)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>시각</TableCell>
              <TableCell>이벤트</TableCell>
              <TableCell>구분</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>기기</TableCell>
              <TableCell>결과</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.loginHistory.length === 0 && <EmptyRow cols={6} text="이력이 없습니다." />}
            {detail.loginHistory.map((h) => (
              <TableRow key={h.id}>
                <TableCell>{formatKstDateTime(h.createdAt)}</TableCell>
                <TableCell>{h.action}</TableCell>
                <TableCell>
                  <ClientChip client={h.client} />
                </TableCell>
                <TableCell>{h.ipAddress}</TableCell>
                <TableCell>{summarizeUserAgent(h.userAgent)}</TableCell>
                <TableCell>
                  <Chip size="small" label={h.success ? '성공' : h.message || '실패'} color={h.success ? 'success' : 'error'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

const AgentsTab = ({ detail, reload }) => {
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const revoke = (a) =>
    ask({
      title: '에이전트 폐기',
      message: `'${a.name || a.agentId}' 에이전트의 인증을 폐기합니다.\n그 PC는 중앙 서버와 동기화할 수 없게 되며, 다시 쓰려면 에이전트에서 페어링 파일을 지우고 재로그인해야 합니다.`,
      confirmText: '폐기',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/agents/${a.agentId}/revoke`, { userId: detail.user.id });
          notify(res.data.message);
          reload();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });
  return (
    <Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>에이전트 ID</TableCell>
            <TableCell>PC 이름</TableCell>
            <TableCell>버전</TableCell>
            <TableCell>상태</TableCell>
            <TableCell>마지막 동기화</TableCell>
            <TableCell>IP</TableCell>
            <TableCell>등록</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.agents.length === 0 && <EmptyRow cols={8} text="등록된 에이전트가 없습니다." />}
          {detail.agents.map((a) => (
            <TableRow key={a.agentId}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{a.agentId}</TableCell>
              <TableCell>{a.name || '-'}</TableCell>
              <TableCell>{a.version || '-'}</TableCell>
              <TableCell>
                {a.revokedAt ? (
                  <Tooltip title={a.revokedReason || ''}>
                    <Chip size="small" label="폐기됨" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Chip size="small" label={a.online ? '접속중' : '오프라인'} color={a.online ? 'success' : 'default'} variant="outlined" />
                )}
              </TableCell>
              <TableCell>{formatRelative(a.lastSeenAt)}</TableCell>
              <TableCell>{a.lastIp || '-'}</TableCell>
              <TableCell>{formatKstDateTime(a.createdAt, { seconds: false })}</TableCell>
              <TableCell align="right">
                {!a.revokedAt && (
                  <Button size="small" color="error" onClick={() => revoke(a)}>
                    폐기
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {confirmDialog}
    </Paper>
  );
};

const NotifyTab = ({ detail, reload }) => {
  const notify = useNotify();
  const t = detail.telegram;
  const unlink = async () => {
    try {
      const res = await apiClient.delete(`/admin/users/${encodeURIComponent(detail.user.id)}/telegram`);
      notify(res.data.message);
      reload();
    } catch (e) {
      notify(getErrorMessage(e), 'error');
    }
  };
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        텔레그램 알림
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        상태:{' '}
        {t.hasChatId ? (
          <Chip size="small" color="success" label={`연결됨 (chat_id ${t.chatId})`} />
        ) : t.linkPending ? (
          <Chip size="small" color="warning" label="연결 대기 중 (딥링크 발급됨)" />
        ) : (
          <Chip size="small" label="연결 안 됨" />
        )}
      </Typography>
      <Button variant="outlined" color="error" disabled={!t.hasChatId && !t.linkPending} onClick={unlink}>
        연결 해제
      </Button>
    </Paper>
  );
};

const AuditTab = ({ detail }) => (
  <Paper>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>시각</TableCell>
          <TableCell>관리자</TableCell>
          <TableCell>작업</TableCell>
          <TableCell>내용</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {detail.auditLogs.length === 0 && <EmptyRow cols={4} text="관리자 작업 기록이 없습니다." />}
        {detail.auditLogs.map((l) => (
          <TableRow key={l.id}>
            <TableCell>{formatKstDateTime(l.createdAt)}</TableCell>
            <TableCell>{l.adminUsername || l.adminUserId}</TableCell>
            <TableCell>{auditLabel(l.action)}</TableCell>
            <TableCell>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {l.detail ? JSON.stringify(l.detail) : '-'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const TABS = [
  { label: '기본 정보', Comp: ProfileTab },
  { label: '구독', Comp: SubscriptionTab },
  { label: '접속 정보', Comp: AccessTab },
  { label: '에이전트', Comp: AgentsTab },
  { label: '알림', Comp: NotifyTab },
  { label: '관리자 작업', Comp: AuditTab },
];

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/admin/users/${encodeURIComponent(id)}`);
      setDetail(res.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const { Comp } = TABS[tab];
  const u = detail?.user;
  const liveSessions = (detail?.sessions || []).filter(
    (s) => s.isActive && (!s.expiresAt || new Date(s.expiresAt) > new Date())
  ).length;
  const onlineAgents = (detail?.agents || []).filter((a) => a.online).length;

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>
        목록으로
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!detail && !error && <LinearProgress />}
      {detail && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {u.username || '-'}
              </Typography>
              <Typography color="text.secondary">
                {u.email} · #{u.id}
              </Typography>
              <UserStatusChip status={u.status} />
              <SubscriptionChip
                status={detail.subscription.subscription === 'Y' ? 'active' : 'none'}
                planName={detail.subscription.subscriptionPlanName}
                expiresAt={detail.subscription.subscriptionExpiresAt}
              />
              {u.isAdmin && <Chip size="small" color="secondary" label="관리자" />}
              <Chip size="small" variant="outlined" color={liveSessions ? 'success' : 'default'} label={`접속 세션 ${liveSessions}`} />
              <Chip size="small" variant="outlined" color={onlineAgents ? 'success' : 'default'} label={`에이전트 접속 ${onlineAgents}`} />
            </Box>
          </Paper>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            {TABS.map((t) => (
              <Tab key={t.label} label={t.label} />
            ))}
          </Tabs>
          <Comp detail={detail} reload={load} />
        </>
      )}
    </>
  );
};

export default UserDetail;
